// Vertical split layout that hosts the editor on top and the
// SimulationPanel as a bottom dock. EpxDmn owns one instance and forwards
// open/close/setStorageKey calls.
//
// Persists the dock height per host-supplied storageKey:
//   localStorage[`${storageKey}.dockRatio`] → fraction in (0,1)
//
// The editor is mounted into `editorAreaEl` by EpxDmn; this class only
// manages the dock div, splitter, and lazy-mount of SimulationPanel.

import { SimulationPanel } from '../simulator/SimulationPanel';

const MIN_EDITOR_PX = 120;
const MIN_DOCK_PX = 120;
const SPLITTER_PX = 6;

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function safeParseRatio(raw, fallback) {
  const n = parseFloat(raw);
  if (!isFinite(n) || n <= 0 || n >= 1) return fallback;
  return n;
}

export class SimulatorDock {
  constructor({
    rootEl,
    editorAreaEl,
    epxDmn,
    storageKey,
    defaultRatio = 0.45,
    onRevealCell = null,
    onToggle = null,
    onResize = null,
  }) {
    this._rootEl = rootEl;
    this._editorAreaEl = editorAreaEl;
    this._epx = epxDmn;
    this._storageKey = storageKey || null;
    this._defaultRatio = defaultRatio;
    this._onRevealCell = onRevealCell;
    this._onToggle = onToggle;
    this._onResize = onResize;

    this._panel = null;
    this._open = false;
    this._dragging = false;

    this._splitterEl = document.createElement('div');
    this._splitterEl.className = 'epx-sim-splitter';
    this._splitterEl.setAttribute('role', 'separator');
    this._splitterEl.setAttribute('aria-orientation', 'horizontal');

    this._dockEl = document.createElement('div');
    this._dockEl.className = 'epx-sim-dock';

    this._rootEl.appendChild(this._splitterEl);
    this._rootEl.appendChild(this._dockEl);

    this._applyRatio(this._loadRatio());

    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._splitterEl.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);
  }

  isOpen() { return this._open; }

  open() {
    if (this._open) return;
    this._open = true;
    this._rootEl.classList.add('epx-sim-open');
    this._mountPanelIfNeeded();
    this._applyRatio(this._loadRatio());
    this._notifyResize();
    if (this._onToggle) this._onToggle({ open: true });
  }

  close() {
    if (!this._open) return;
    this._open = false;
    this._rootEl.classList.remove('epx-sim-open');
    if (this._panel && typeof this._panel._clearDrd === 'function') {
      try { this._panel._clearDrd(); } catch { /* noop */ }
    }
    this._notifyResize();
    if (this._onToggle) this._onToggle({ open: false });
  }

  toggle() {
    if (this._open) this.close();
    else this.open();
  }

  setStorageKey(key) {
    if (key === this._storageKey) return;
    this._storageKey = key || null;
    if (this._panel && typeof this._panel.setStorageKey === 'function') {
      this._panel.setStorageKey(key);
    }
    this._applyRatio(this._loadRatio());
  }

  destroy() {
    this._splitterEl.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mouseup', this._onMouseUp);
    if (this._panel && typeof this._panel.destroy === 'function') {
      try { this._panel.destroy(); } catch { /* noop */ }
    }
    this._panel = null;
    if (this._splitterEl.parentNode) this._splitterEl.parentNode.removeChild(this._splitterEl);
    if (this._dockEl.parentNode) this._dockEl.parentNode.removeChild(this._dockEl);
  }

  _mountPanelIfNeeded() {
    if (this._panel) return;
    this._panel = new SimulationPanel(this._dockEl, {
      epxDmn: this._epx,
      storageKey: this._storageKey || 'epx-dmn.simulation.default',
      onRevealCell: this._onRevealCell,
      onClose: () => this.close(),
    });
  }

  _onMouseDown(e) {
    if (!this._open) return;
    this._dragging = true;
    document.body.style.cursor = 'ns-resize';
    e.preventDefault();
  }

  _onMouseMove(e) {
    if (!this._dragging) return;
    const rect = this._rootEl.getBoundingClientRect();
    const total = rect.height;
    if (total <= 0) return;
    // Pointer Y relative to root → splitter sits at this Y; dock height = total - y - splitter.
    const y = e.clientY - rect.top;
    const dockHeight = clamp(
      total - y - SPLITTER_PX,
      MIN_DOCK_PX,
      total - MIN_EDITOR_PX - SPLITTER_PX,
    );
    const ratio = dockHeight / total;
    this._applyRatio(ratio);
  }

  _onMouseUp() {
    if (!this._dragging) return;
    this._dragging = false;
    document.body.style.cursor = '';
    this._saveRatio(this._currentRatio);
    this._notifyResize();
  }

  _applyRatio(ratio) {
    this._currentRatio = clamp(ratio, 0.05, 0.95);
    this._dockEl.style.flex = `0 0 ${(this._currentRatio * 100).toFixed(2)}%`;
  }

  _ratioStorageKey() {
    return this._storageKey ? `${this._storageKey}.dockRatio` : null;
  }

  _loadRatio() {
    if (typeof localStorage === 'undefined') return this._defaultRatio;
    const k = this._ratioStorageKey();
    if (!k) return this._defaultRatio;
    return safeParseRatio(localStorage.getItem(k), this._defaultRatio);
  }

  _saveRatio(ratio) {
    if (typeof localStorage === 'undefined') return;
    const k = this._ratioStorageKey();
    if (!k) return;
    try { localStorage.setItem(k, String(ratio)); } catch { /* noop */ }
  }

  _notifyResize() {
    if (this._onResize) {
      requestAnimationFrame(() => this._onResize());
    }
  }
}
